import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            padding: 0,
            margin: 0,
        },
    },
    formControl: {
        margin: 0,
        minWidth: 80,
        [theme.breakpoints.down('xs')]: {
            minWidth: 'auto',
        },
    },
    selectButton: {
        backgroundColor: "#77777726",
        // Cap width so multiple tags can't push surrounding action buttons
        // off-screen (was breaking the "Move to" pill onto two lines).
        maxWidth: 220,
        "& .MuiSelect-outlined": {
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 0,
        },
        // Mobile ONLY - equal cells
        [theme.breakpoints.down('xs')]: {
            height: 36,
            borderRadius: 18,
            backgroundColor: theme.palette.grey[100],
            "& .MuiSelect-outlined": {
                padding: '6px 10px',
                paddingRight: 26,
                fontSize: '0.8rem',
            },
        },
    },
}));

export function TagsContainer({ ticket }) {
    const classes = useStyles();
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        const initializeTags = async () => {
            await loadTags();
            if (Array.isArray(ticket.tags)) {
                const matchedTags = ticket.tags.map((ticketTag) =>
                    tags.find((tag) => tag.id === ticketTag.id) || ticketTag
                );
                setSelectedTags(matchedTags);
            } else {
                setSelectedTags([]);
            }
        };

        if (isMounted.current) {
            initializeTags();
        }
    }, [ticket]);

    useEffect(() => {
        if (Array.isArray(ticket.tags)) {
            const matchedTags = ticket.tags.map((ticketTag) =>
                tags.find((tag) => tag.id === ticketTag.id) || ticketTag
            );
            setSelectedTags(matchedTags);
        }
    }, [tags]);

    const loadTags = async () => {
        try {
            const { data: loadedTags } = await api.get(`/tags/list`);
            if (isMounted.current) {
                setTags(loadedTags);
            }
        } catch (err) {
            toastError(err);
        }
    };

    const syncTags = async (data) => {
        try {
            await api.post(`/tags/sync`, data);
        } catch (err) {
            toastError(err);
        }
    };

    const handleChange = async (event) => {
        const { value } = event.target;
        setSelectedTags(value);
        await syncTags({ ticketId: ticket.id, tags: value });
    };

    return (
        <Box className={classes.container}>
            <FormControl className={classes.formControl} margin="none">
                <Select
                    className={classes.selectButton}
                    multiple
                    displayEmpty
                    variant="outlined"
                    value={selectedTags}
                    onChange={handleChange}
                    renderValue={(selected) => {
                        if (selected.length === 0) {
                            return <em>תגיות</em>;
                        }
                        if (selected.length === 1) {
                            return selected[0].name;
                        }
                        // Multiple tags — show first + "+N" badge so the
                        // chip stays compact and doesn't shove the
                        // surrounding action buttons around.
                        return `${selected[0].name} +${selected.length - 1}`;
                    }}
                >
                    {tags.map((tag) => (
                        <MenuItem key={tag.id} value={tag}>
                            <Checkbox
                                style={{ color: tag.color || "#eee" }}
                                size="small"
                                color="primary"
                                checked={selectedTags.some(
                                    (selected) => selected.id === tag.id
                                )}
                            />
                            <ListItemText primary={tag.name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
}

export default TagsContainer;
